/**
 * JWT utility functions for frontend
 */

export interface JwtPayload {
  userId: string;
  email: string;
  role?: string;
  twoFactorVerified?: boolean;
  iat: number;
  exp: number;
}

/**
 * Decode a JWT token without verifying signature
 * (signature verification happens on the backend)
 */
export function decodeToken(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const encodedPayload = parts[1];
    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const decodedPayload = atob(paddedBase64);
    
    return JSON.parse(decodedPayload) as JwtPayload;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Check if a token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp < now;
}

/**
 * Get the expiration time of a token in seconds
 */
export function getTokenExpiration(token: string): number | null {
  const payload = decodeToken(token);
  return payload ? payload.exp : null;
}

/**
 * Check if token will expire soon (within the specified minutes)
 */
export function willTokenExpireSoon(token: string, minutes: number = 60): boolean {
  const payload = decodeToken(token);
  if (!payload) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  const threshold = now + (minutes * 60);
  return payload.exp < threshold;
}

/**
 * Get time remaining until token expiration in seconds
 */
export function getTimeUntilExpiration(token: string): number {
  const payload = decodeToken(token);
  if (!payload) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  return Math.max(0, payload.exp - now);
}
